import React, { useState } from "react";
import { Button, Spinner } from "react-bootstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import useUpdateDeal from "../hooks/useUpdateDeal";
import { REQUIRED_FIELD } from "../../../../core/data/constants/constant";
import {
  updateDealsListCache,
  updateSingleDealCache,
} from "../actions/updateDealsListCache";
import { generateQueryParams } from "../../../../utils/helpers/generateQueryParams";
import { useSelector } from "react-redux";
import { getAllUrlParams } from "../../../../utils/helpers/setAndGetUrlParams";

const PaymentApprovalModal = ({ dealData, isUserAccountant }) => {
  const mutationUpdate = useUpdateDeal();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const urlParams = getAllUrlParams();

  const { filters } = useSelector((state) => state.filter);
  const dealsFilters = filters.deals || [];
  let { queryParams, params } = generateQueryParams(dealsFilters);

  const validationSchema = Yup.object({
    paymentDate: Yup.date().required(REQUIRED_FIELD),
    paymentId: Yup.string().required(REQUIRED_FIELD),
    salesOrderNumber: Yup.string(),
    oldPaymentRemark: Yup.string().nullable(),
    amountPaid: Yup.number()
      .required(REQUIRED_FIELD)
      .positive("Amount must be positive"),
  });

  const formik = useFormik({
    initialValues: {
      paymentDate: new Date().toISOString().split("T")[0], // Default to today's date
      paymentId: "",
      salesOrderNumber: "",
      oldPaymentRemark: "",
      amountPaid: "",
      amount: 0,
      paymentStatus: "Pending",
    },
    validationSchema,
    onSubmit: (values) => {
      setIsSubmitting(true); // Show loader

      const updatedVerificationRecord = [
        ...(dealData?.verificationRecord || []),
        values,
      ];

      const totalAmount = updatedVerificationRecord.reduce(
        (sum, record) =>
          record.paymentStatus === "Approved"
            ? sum + (Number(record.amountPaid) || 0)
            : sum,
        0
      );

      const updatedPaymentDetails = {
        ...(dealData?.paymentDetails || {}),
        amount: totalAmount,
      };

      const timelineMessage = `Payment approval requested for <b>Payment ID: ${values.paymentId}</b>`;

      mutationUpdate.mutate(
        {
          id: dealData?._id,
          dealData: {
            verificationRecord: updatedVerificationRecord,
            dealSentForApprovalToAccountant: true,
            paymentDetails: updatedPaymentDetails,
            textMessageAboutActivity: timelineMessage,
          },
        },
        {
          onSuccess: (res, { id }) => {
            updateSingleDealCache(id, res.data);

            updateDealsListCache({
              id,
              updatedData: res.data,
              page: urlParams?.page * 1,
              limit: urlParams?.limit * 1,
              queryString: params?.toString(),
              queryParams: queryParams?.toString(),
              companyId: urlParams?.companyid,
            });
            setIsSubmitting(false); // Hide loader
            document.getElementById("closeModalBtn").click();
            formik.resetForm();
          },
          onError: () => {
            setIsSubmitting(false); // Hide loader
          },
        }
      );
    },
  });

  return (
    <div
      className="modal fade"
      id="paymentApprovalModal"
      tabIndex="-1"
      aria-labelledby="paymentApprovalModalLabel"
      aria-hidden="true"
    >
      <div className="modal-dialog">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title" id="paymentApprovalModalLabel">
              Request Payment Approval
            </h5>
            <button
              type="button"
              className="btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
              id="closeModalBtn"
            ></button>
          </div>
          <form onSubmit={formik.handleSubmit}>
            <div className="modal-body">
              <div className="mb-3">
                <label className="form-label">
                  Payment Date<span className="text-danger">*</span>
                </label>
                <input
                  type="date"
                  name="paymentDate"
                  className="form-control"
                  value={formik.values.paymentDate}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.paymentDate && formik.errors.paymentDate && (
                  <div className="text-danger">{formik.errors.paymentDate}</div>
                )}
              </div>

              <div className="mb-3">
                <label className="form-label">
                  Payment ID<span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  name="paymentId"
                  className="form-control"
                  value={formik.values.paymentId}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.paymentId && formik.errors.paymentId && (
                  <div className="text-danger">{formik.errors.paymentId}</div>
                )}
              </div>

              <div className="mb-3">
                <label className="form-label">Sales Order Number</label>
                <input
                  type="text"
                  name="salesOrderNumber"
                  className="form-control"
                  value={formik.values.salesOrderNumber}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.salesOrderNumber &&
                  formik.errors.salesOrderNumber && (
                    <div className="text-danger">
                      {formik.errors.salesOrderNumber}
                    </div>
                  )}
              </div>

              <div className="mb-3">
                <label className="form-label">Old Payment Remarks</label>
                <input
                  type="text"
                  name="oldPaymentRemark"
                  className="form-control text-capitalize"
                  value={formik.values.oldPaymentRemark}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.oldPaymentRemark &&
                  formik.errors.oldPaymentRemark && (
                    <div className="text-danger">
                      {formik.errors.oldPaymentRemark}
                    </div>
                  )}
              </div>

              <div className="mb-3">
                <label className="form-label">
                  Amount Paid<span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  name="amountPaid"
                  className="form-control"
                  value={formik.values.amountPaid}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.amountPaid && formik.errors.amountPaid && (
                  <div className="text-danger">{formik.errors.amountPaid}</div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <Button variant="secondary" data-bs-dismiss="modal">
                Close
              </Button>
              <Button type="submit" variant="primary" disabled={isSubmitting}>
                {isSubmitting ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  "Submit"
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PaymentApprovalModal;
